// Flight-supplier picker for the top right of the search card.
//
// A native <select> renders its list with OS chrome (system highlight colour,
// system font, system corners), which is the one control on this page that
// wouldn't belong to the design. This is the same popover pattern the rest of
// the search card already uses — see AirportPicker's `.airport-suggest` list
// and the `.field-popover` on Travellers · cabin.
//
// Keyboard: ↑ ↓ move the active item, Enter selects, Esc closes.

import { useEffect, useRef, useState } from "react";
import { DEFAULT_FLIGHT_SOURCE, FLIGHT_SOURCES, type FlightSource } from "../lib/types";

interface Props {
  value: FlightSource;
  onChange: (source: FlightSource) => void;
  /** True when this control should render its open state. */
  isActive: boolean;
  onActivate: () => void;
  onClose: () => void;
}

export function SourcePicker(props: Props) {
  const selectedIdx = Math.max(FLIGHT_SOURCES.findIndex((s) => s.value === props.value), 0);
  const [activeIdx, setActiveIdx] = useState(selectedIdx);
  const menuRef = useRef<HTMLDivElement>(null);

  // Open on the current selection so ↑ ↓ start from where the user is.
  useEffect(() => {
    if (props.isActive) {
      setActiveIdx(selectedIdx);
      requestAnimationFrame(() => menuRef.current?.focus());
    }
  }, [props.isActive, selectedIdx]);

  function pick(source: FlightSource) {
    props.onChange(source);
    props.onClose();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(FLIGHT_SOURCES.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const option = FLIGHT_SOURCES[activeIdx];
      if (option) pick(option.value);
    } else if (e.key === "Escape") {
      e.preventDefault();
      props.onClose();
    }
  }

  const selected = FLIGHT_SOURCES[selectedIdx];

  return (
    <div className="source-picker" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`source-trigger${props.isActive ? " active" : ""}`}
        onClick={() => (props.isActive ? props.onClose() : props.onActivate())}
        aria-haspopup="listbox"
        aria-expanded={props.isActive}
      >
        <span className="source-label">Source</span>
        <span className="source-value">{selected?.label}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {props.isActive && (
        <div
          className="source-menu"
          role="listbox"
          aria-label="Flight source"
          tabIndex={-1}
          ref={menuRef}
          onKeyDown={onKeyDown}
        >
          {FLIGHT_SOURCES.map((option, i) => (
            <div
              key={option.value}
              role="option"
              aria-selected={option.value === props.value}
              className={`source-menu-item${i === activeIdx ? " active" : ""}${option.value === props.value ? " selected" : ""}`}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => pick(option.value)}
            >
              <span className="name">{option.label}</span>
              {option.value === DEFAULT_FLIGHT_SOURCE && <span className="hint">Default</span>}
              <svg className="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
